import { FC } from 'react'
import { useRouter } from 'next/router'
import { useSetAtom } from 'jotai'
import { HeartFilledIcon, HeartIcon } from 'icons'

import useAuth from '../../hooks/useAuth'
import { useSaveFavorite } from '../../services/customer/saveFavorite'
import { useTracking } from '../../services/context/TrackingContext'
import { usePropertyTracking } from '../../hooks/useAnalyticsIdentification'
import { customerFavoriteListings } from '../../utils/atoms'

const FavoriteButton: FC<{
  listingId: number
  trackLinkLocation: string
  onSave: () => void
  active?: boolean
  className?: string
  propertyData?: {
    price: number
    bedrooms: number
    bathrooms: number
    location: string
  }
}> = ({ listingId, trackLinkLocation, onSave, active, className, propertyData }) => {
  const { isAuthenticated, user } = useAuth()
  const { track } = useTracking()
  const router = useRouter()
  
  // Initialize property analytics tracking
  const propertyTracking = usePropertyTracking(user?.email || '')

  const setCustomerFavoriteListings = useSetAtom(customerFavoriteListings)

  const { mutate: saveFavorite } = useSaveFavorite()

  const handleClick = (action: 'add' | 'remove') => {
    if (!isAuthenticated) {
      track({
        event: 'Sign Up Flow Opened',
        properties: {
          link_location: trackLinkLocation,
          link_type: 'Icon',
          link_description: 'Save A Property Listing - Tap Heart Icon',
        },
      })

      router.push({
        pathname: '/sign-up',
        query: { returnUrl: `/homes/${listingId}/details` },
      })
    } else {
      // Track favorite action in analytics
      if (propertyTracking && propertyData && user) {
        propertyTracking.trackFavorite(listingId.toString(), action === 'add' ? 'added' : 'removed')
      }

      // @TODO handle revert state change if request fails
      setCustomerFavoriteListings((existingFavorites) => {
        if (action === 'remove') {
          return existingFavorites.filter(
            (e: { id: number; date: string }) => e.id !== listingId
          )
        } else {
          onSave()

          return [
            ...existingFavorites,
            {
              id: listingId,
              date: new Date().toISOString(),
            },
          ]
        }
      })
      saveFavorite({ listingId, remove: action === 'remove' })
    }
  }

  return isAuthenticated && active ? (
    <button className={className} onClick={() => handleClick('remove')}>
      <span className="sr-only">Favorite</span>
      <HeartFilledIcon aria-hidden="true" className="stroke-teal-5 rect-24" />
    </button>
  ) : (
    <button className={className} onClick={() => handleClick('add')}>
      <span className="sr-only">Favorite</span>
      <HeartIcon aria-hidden="true" className="stroke-teal-5 rect-24" />
    </button>
  )
}

export default FavoriteButton
